import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    useEffect,
    useRef,
    useState,
} from "preact/compat";

type MediaPreviewPopupProps = {
    item: MediaItem;
    position: { x: number; y: number; };
    onClose: () => void;
};

export const MediaPreviewPopup = ({
    item,
    position,
}: MediaPreviewPopupProps) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [adjustedPosition, setAdjustedPosition] = useState(position);

    // Adjust position to keep popup within viewport
    useEffect(() => {
        if (!popupRef.current) return;

        const popup = popupRef.current;
        const rect = popup.getBoundingClientRect();
        const viewportWidth = globalThis.innerWidth;
        const viewportHeight = globalThis.innerHeight;

        let x = position.x + 10;
        let y = position.y + 10;

        // Adjust if going off right edge
        if (x + rect.width > viewportWidth - 20) {
            x = position.x - rect.width - 10;
        }

        // Adjust if going off bottom edge
        if (y + rect.height > viewportHeight - 20) {
            y = viewportHeight - rect.height - 20;
        }

        // Ensure not going off left or top
        x = Math.max(10, x);
        y = Math.max(10, y);

        setAdjustedPosition({
            x,
            y,
        });
    }, [position]);

    return (
        <div
            ref={popupRef}
            className="fixed z-50 bg-bg-secondary border border-border rounded-lg shadow-xl p-2 pointer-events-none"
            style={{
                left: adjustedPosition.x,
                top: adjustedPosition.y,
                maxWidth: "400px",
                maxHeight: "400px",
            }}
        >
            {item.media_type === "video"
                ? (
                    <video
                        src={item.media_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="max-w-full max-h-[380px] rounded"
                    />
                )
                : (
                    <img
                        src={item.media_url || item.thumbnail_url}
                        alt={`Preview ${item.id}`}
                        className="max-w-full max-h-[380px] rounded object-contain"
                    />
                )}
        </div>
    );
};

// Hook for managing preview popup state
export const usePreviewPopup = () => {
    const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
    const [previewPosition, setPreviewPosition] = useState({
        x: 0,
        y: 0,
    });
    const hideTimeoutRef = useRef<number | null>(null);

    const showPreview = (item: MediaItem, e: MouseEvent) => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setPreviewItem(item);
        setPreviewPosition({
            x: e.clientX,
            y: e.clientY,
        });
    };

    const updatePosition = (e: MouseEvent) => {
        setPreviewPosition({
            x: e.clientX,
            y: e.clientY,
        });
    };

    const hidePreview = () => {
        hideTimeoutRef.current = globalThis.setTimeout(() => {
            setPreviewItem(null);
        }, 100);
    };

    const cancelHide = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    return {
        previewItem,
        previewPosition,
        showPreview,
        hidePreview,
        updatePosition,
        cancelHide,
    };
};
